import noop from 'utils/noop';
import { createContext, useContext, useMemo } from 'react';
import { EspAccount } from 'modules/graphql';
import {
  isIterableSmsProviderAccount,
  isTelnyxDirectAccount,
  isTwilioDirectAccount,
} from 'modules/sendingPlatforms/utils';
import { DateType } from 'ui/datePicker';
import { getNewDate, displayDate, DATE_FORMAT_FULL } from 'utils/date';
import { ITERABLE_SMS, TELNYX_DIRECT, TWILIO_DIRECT } from '../constants';
import { Metric, Project } from '../types';
import { pickMetric } from './BillingQueryContext.utils';
import { isOrgMetric, isProjectMetric } from '../utils';
import { getJvtCountPerProjectPerJourney } from '../components/Index/journeyEvents/JourneyEventsCardUtils';

type DateRange = {
  startDate: DateType;
  endDate: DateType;
};

export type BillingQueryContextState = {
  metrics: Metric[];
  dateRange: DateRange;
  setDateRange: ({ startDate, endDate }: DateRange) => void;
  isError: boolean;
  isLoading: boolean;
  espAccounts: EspAccount[];
};

const defaultState = {
  metrics: [],
  dateRange: { startDate: getNewDate(), endDate: getNewDate() },
  setDateRange: noop,
  isError: false,
  isLoading: false,
  espAccounts: [],
};

export const BillingQueryContext = createContext<BillingQueryContextState>(defaultState);

export const useBillingQueryContext = () => {
  const { metrics, dateRange, setDateRange, isError, isLoading, espAccounts } =
    useContext(BillingQueryContext);

  /**
   * Org Metric Pickers
   * ==========================================================================
   * ==========================================================================
   */
  const orgMetrics = metrics?.filter(isOrgMetric);

  const orgMetricsByMetricName = orgMetrics.reduce<Record<string, Metric | Metric[]>>(
    (acc, orgMetric) => {
      if (!acc[orgMetric.metricName]) {
        acc[orgMetric.metricName] = orgMetric;
      } else if (Array.isArray(acc[orgMetric.metricName])) {
        /** If there are 3 or more metrics with the same metricName */
        acc[orgMetric.metricName] = [...(acc[orgMetric.metricName] as Metric[]), orgMetric];
      } else {
        /**
         * If there are 2 metrics with the same metricName -- they are grouped
         * so that they can be displayed under a collapsible grouping.
         */
        acc[orgMetric.metricName] = [acc[orgMetric.metricName] as Metric, orgMetric];
      }
      return acc;
    },
    {},
  );

  const pickOrgMetrics = (metricName: string, filterFn?: (metric: Metric) => boolean) =>
    typeof filterFn === 'function'
      ? (orgMetricsByMetricName[metricName] as Metric[])?.filter(filterFn) || []
      : (orgMetricsByMetricName[metricName] as Metric[]) || [];

  const pickOrgMetric = pickMetric(orgMetricsByMetricName);

  const pickOrgValue = (metricName: string, filterFn?: (metric: Metric) => boolean) =>
    pickOrgMetric(metricName, filterFn)?.value || 0;

  const pickManyOrgMetrics = (metricNames: string[], filterFn?: (metric: Metric) => boolean) =>
    metricNames.map((metricName) => pickOrgMetric(metricName, filterFn)).flat();

  const pickJVTbyProjectBreakdownByJourney = (projectId: string) =>
    getJvtCountPerProjectPerJourney(metrics).filter((p) => p.projectId.toString() === projectId);

  /**
   * Project Metric Pickers
   * ==========================================================================
   * ==========================================================================
   */
  const projectMetrics = metrics?.filter(isProjectMetric);

  const projectMetricsByProjectId = projectMetrics.reduce(
    (acc, metric) => {
      const { id: projectId } = metric?.project as Project;
      if (!acc[projectId]) {
        acc[projectId] = [metric];
      } else {
        acc[projectId] = [...acc[projectId], metric];
      }
      return acc;
    },
    {} as Record<number, Metric[]>,
  );

  const projectIds = Object.keys(projectMetricsByProjectId).map(Number);

  const projectMetricsByProjectIdByMetricName = projectIds.reduce(
    (byId, projectId) => {
      const metricsByMetricName = projectMetricsByProjectId[projectId].reduce(
        (byName, metric) => {
          const current = byName[metric.metricName];
          if (byName[metric.metricName]) {
            const next = Array.isArray(current) ? [...current, metric] : [current, metric];
            return { ...byName, [metric.metricName]: next };
          } else {
            return { ...byName, [metric.metricName]: metric };
          }
        },
        {} as Record<string, Metric | Metric[]>,
      );
      return { ...byId, [projectId]: metricsByMetricName };
    },
    {} as Record<number, Record<string, Metric | Metric[]>>,
  );

  const pickProjectMetric = (
    projectId: number,
    metricName: string,
    filterFn?: (metric: Metric) => boolean,
  ) => pickMetric(projectMetricsByProjectIdByMetricName[projectId])(metricName, filterFn);

  const pickManyProjectMetrics = (
    projectId: number,
    metricNames: string[],
    filterFn?: (metric: Metric) => boolean,
  ) => metricNames.map((metricName) => pickProjectMetric(projectId, metricName, filterFn)).flat();

  /**
   * Other Utilities
   * ==========================================================================
   * ==========================================================================
   */
  const isEnabledByProvider = useMemo(
    /**
     * Check for ESP accounts associated with a given provider. This check is
     * used to determine whether metrics associated with those providers should
     * be shown, because the usageV2 endpoint does not automatically filter out
     * those metrics.
     */
    () => ({
      [ITERABLE_SMS]: Boolean(espAccounts.find(isIterableSmsProviderAccount)),
      [TELNYX_DIRECT]: Boolean(espAccounts.find(isTelnyxDirectAccount)),
      [TWILIO_DIRECT]: Boolean(espAccounts.find(isTwilioDirectAccount)),
    }),
    [espAccounts],
  );

  const displayStartDate = displayDate(dateRange.startDate, DATE_FORMAT_FULL);
  const displayEndDate = displayDate(dateRange.endDate, DATE_FORMAT_FULL);
  const displayDateRange = `${displayStartDate} - ${displayEndDate}`;

  return {
    isError,
    isLoading,
    dateRange,
    setDateRange,
    displayDateRange,
    displayStartDate,
    displayEndDate,
    orgMetrics,
    orgMetricsByMetricName,
    pickOrgMetric,
    pickOrgMetrics,
    pickOrgValue,
    projectMetrics,
    projectMetricsByProjectId,
    projectIds,
    pickManyOrgMetrics,
    pickProjectMetric,
    pickManyProjectMetrics,
    isEnabledByProvider,
    pickJVTbyProjectBreakdownByJourney,
  };
};
